// Math, geometry and everyday-measurement calculators on the shared calculator engine
// (BrowserFinanceCalculator + FinanceMeta). Roadmap wave 2026-12. Every formula here is
// checked against hand-worked values in scripts before shipping.

import type { FinanceField, FinanceMeta } from '@/lib/browser-finance-tools';
import type { MathPackSlug } from '@/lib/math-pack-slugs';

const NOTE = 'Calculations run locally in your browser; nothing is uploaded.';

const num = (key: string, label: string, def: string): FinanceField => ({
  key,
  label,
  placeholder: def,
  default: def,
});
const select = (
  key: string,
  label: string,
  def: string,
  options: Array<[string, string]>,
): FinanceField => ({
  key,
  label,
  placeholder: '',
  default: def,
  kind: 'select',
  options: options.map(([l, value]) => ({ label: l, value })),
});

function fmt(value: number, digits = 6): string {
  if (!Number.isFinite(value)) return 'undefined';
  if (value !== 0 && (Math.abs(value) >= 1e15 || Math.abs(value) < 1e-6))
    return value.toExponential(6).replace(/\.?0+e/, 'e');
  return value.toLocaleString('en-US', { maximumFractionDigits: digits });
}

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a));
  let y = Math.abs(Math.round(b));
  while (y) [x, y] = [y, x % y];
  return x;
}
const lcm2 = (a: number, b: number) =>
  a && b ? Math.abs(Math.round(a) * Math.round(b)) / gcd(a, b) : 0;

function primeFactors(n: number): number[] {
  const out: number[] = [];
  let x = Math.abs(Math.round(n));
  for (let p = 2; p * p <= x; p += p === 2 ? 1 : 2) {
    while (x % p === 0) {
      out.push(p);
      x /= p;
    }
  }
  if (x > 1) out.push(x);
  return out;
}

function factorString(n: number): string {
  const f = primeFactors(n);
  if (!f.length) return String(n);
  const counts = new Map<number, number>();
  for (const p of f) counts.set(p, (counts.get(p) ?? 0) + 1);
  return [...counts].map(([p, c]) => (c > 1 ? `${p}^${c}` : String(p))).join(' × ');
}

const inputs = (v: Record<string, number>, keys: string[]) =>
  keys.map((k) => Math.round(v[k])).filter((x) => Number.isFinite(x) && x > 0);

const deg = (rad: number) => (rad * 180) / Math.PI;

export const mathPackMeta: Record<MathPackSlug, FinanceMeta> = {
  'lcm-calculator': {
    key: 'lcm-calculator',
    title: 'LCM Calculator',
    badge: 'LCM',
    note: NOTE,
    fields: [
      num('a', 'Number 1', '12'),
      num('b', 'Number 2', '18'),
      num('c', 'Number 3 (optional)', '0'),
      num('d', 'Number 4 (optional)', '0'),
    ],
    compute: (v) => {
      const ns = inputs(v, ['a', 'b', 'c', 'd']);
      if (ns.length < 2)
        return [{ label: 'LCM', value: 'Enter at least two whole numbers above 0' }];
      const l = ns.reduce(lcm2);
      const g = ns.reduce(gcd);
      return [
        { label: 'Least common multiple (LCM)', value: fmt(l) },
        { label: 'Greatest common factor (GCF)', value: fmt(g) },
        ...ns.map((n) => ({ label: `Prime factors of ${n}`, value: factorString(n) })),
      ];
    },
  },
  'gcf-calculator': {
    key: 'gcf-calculator',
    title: 'GCF Calculator',
    badge: 'GCF',
    note: NOTE,
    fields: [
      num('a', 'Number 1', '48'),
      num('b', 'Number 2', '180'),
      num('c', 'Number 3 (optional)', '0'),
      num('d', 'Number 4 (optional)', '0'),
    ],
    compute: (v) => {
      const ns = inputs(v, ['a', 'b', 'c', 'd']);
      if (ns.length < 2)
        return [{ label: 'GCF', value: 'Enter at least two whole numbers above 0' }];
      const g = ns.reduce(gcd);
      return [
        { label: 'Greatest common factor (GCF / GCD)', value: fmt(g) },
        { label: 'Least common multiple (LCM)', value: fmt(ns.reduce(lcm2)) },
        ...ns.map((n) => ({ label: `Prime factors of ${n}`, value: factorString(n) })),
      ];
    },
  },
  'factor-calculator': {
    key: 'factor-calculator',
    title: 'Factor Calculator',
    badge: 'Factors',
    note: NOTE,
    fields: [num('n', 'Whole number', '360')],
    compute: ({ n }) => {
      const x = Math.abs(Math.round(n));
      if (!x) return [{ label: 'Factors', value: 'Enter a whole number above 0' }];
      if (x > 1e12) return [{ label: 'Factors', value: 'Enter a number up to 1,000,000,000,000' }];
      const small: number[] = [];
      const large: number[] = [];
      for (let i = 1; i * i <= x; i += 1) {
        if (x % i === 0) {
          small.push(i);
          if (i * i !== x) large.unshift(x / i);
        }
      }
      const all = [...small, ...large];
      const pairs = small.map((s) => `${s} × ${x / s}`);
      return [
        { label: `Factors of ${x}`, value: all.join(', ') },
        { label: 'Number of factors', value: String(all.length) },
        { label: 'Prime factorization', value: factorString(x) },
        { label: 'Factor pairs', value: pairs.join(', ') },
        {
          label: 'Prime?',
          value:
            all.length === 2
              ? 'Yes, prime'
              : x === 1
                ? 'No (1 is neither prime nor composite)'
                : 'No, composite',
        },
      ];
    },
  },
  'slope-calculator': {
    key: 'slope-calculator',
    title: 'Slope Calculator',
    badge: 'Slope',
    note: NOTE,
    fields: [
      num('x1', 'x₁', '1'),
      num('y1', 'y₁', '2'),
      num('x2', 'x₂', '4'),
      num('y2', 'y₂', '8'),
    ],
    compute: ({ x1, y1, x2, y2 }) => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const dist = Math.hypot(dx, dy);
      const mid = `(${fmt((x1 + x2) / 2)}, ${fmt((y1 + y2) / 2)})`;
      if (dx === 0)
        return [
          { label: 'Slope', value: 'Undefined (vertical line)' },
          { label: 'Equation', value: `x = ${fmt(x1)}` },
          { label: 'Distance between points', value: fmt(dist) },
          { label: 'Midpoint', value: mid },
        ];
      const m = dy / dx;
      const b = y1 - m * x1;
      return [
        {
          label: 'Slope (m)',
          value: `${fmt(m)}${Number.isInteger(dy) && Number.isInteger(dx) && !Number.isInteger(m) ? ` (${dy / gcd(dy, dx)}/${dx / gcd(dy, dx)})` : ''}`,
        },
        {
          label: 'Equation',
          value: b ? `y = ${fmt(m)}x ${b < 0 ? '−' : '+'} ${fmt(Math.abs(b))}` : `y = ${fmt(m)}x`,
        },
        { label: 'y-intercept (b)', value: fmt(b) },
        { label: 'Angle of the line', value: `${fmt(deg(Math.atan(m)), 3)}°` },
        { label: 'Distance between points', value: fmt(dist) },
        { label: 'Midpoint', value: mid },
      ];
    },
  },
  'proportion-calculator': {
    key: 'proportion-calculator',
    title: 'Proportion Calculator',
    badge: 'Ratio',
    note: NOTE,
    fields: [num('a', 'A', '3'), num('b', 'B', '4'), num('c', 'C', '12')],
    compute: ({ a, b, c }) => {
      if (!a) return [{ label: 'x', value: 'A cannot be 0' }];
      const x = (b * c) / a;
      return [
        { label: 'x in A/B = C/x', value: fmt(x) },
        { label: 'Cross-multiplication', value: `${fmt(a)} × x = ${fmt(b)} × ${fmt(c)}` },
        {
          label: 'Ratio A:B simplified',
          value:
            Number.isInteger(a) && Number.isInteger(b) && b
              ? `${a / gcd(a, b)}:${b / gcd(a, b)}`
              : fmt(a / b),
        },
      ];
    },
  },
  'quadratic-formula-calculator': {
    key: 'quadratic-formula-calculator',
    title: 'Quadratic Formula Calculator',
    badge: 'Quadratic',
    note: NOTE,
    fields: [
      num('a', 'a (x² term)', '1'),
      num('b', 'b (x term)', '-3'),
      num('c', 'c (constant)', '2'),
    ],
    compute: ({ a, b, c }) => {
      if (!a)
        return b
          ? [{ label: 'x (linear equation)', value: fmt(-c / b) }]
          : [{ label: 'x', value: 'Not an equation in x' }];
      const d = b * b - 4 * a * c;
      const vx = -b / (2 * a);
      const vertex = { label: 'Vertex', value: `(${fmt(vx)}, ${fmt(a * vx * vx + b * vx + c)})` };
      if (d > 0) {
        const r = Math.sqrt(d);
        return [
          { label: 'x₁', value: fmt((-b + r) / (2 * a)) },
          { label: 'x₂', value: fmt((-b - r) / (2 * a)) },
          { label: 'Discriminant (b² − 4ac)', value: `${fmt(d)}: two real roots` },
          vertex,
        ];
      }
      if (d === 0)
        return [
          { label: 'x (double root)', value: fmt(vx) },
          { label: 'Discriminant', value: '0: one repeated root' },
          vertex,
        ];
      const im = Math.sqrt(-d) / (2 * a);
      return [
        { label: 'x₁', value: `${fmt(vx)} + ${fmt(Math.abs(im))}i` },
        { label: 'x₂', value: `${fmt(vx)} − ${fmt(Math.abs(im))}i` },
        { label: 'Discriminant', value: `${fmt(d)}: no real roots, two complex roots` },
        vertex,
      ];
    },
  },
  'pythagorean-theorem-calculator': {
    key: 'pythagorean-theorem-calculator',
    title: 'Pythagorean Theorem Calculator',
    badge: 'a² + b² = c²',
    note: NOTE,
    fields: [
      select('find', 'Find', '0', [
        ['Hypotenuse c (know a and b)', '0'],
        ['Leg a (know b and c)', '1'],
      ]),
      num('p', 'Side a (or b)', '3'),
      num('q', 'Side b (or hypotenuse c)', '4'),
    ],
    compute: ({ find, p, q }) => {
      if (find === 1) {
        if (q <= p) return [{ label: 'a', value: 'The hypotenuse must be the longest side' }];
        return [
          { label: 'Missing leg a', value: fmt(Math.sqrt(q * q - p * p)) },
          { label: 'Working', value: `a = √(${fmt(q)}² − ${fmt(p)}²)` },
        ];
      }
      const c = Math.hypot(p, q);
      return [
        { label: 'Hypotenuse c', value: fmt(c) },
        { label: 'Working', value: `c = √(${fmt(p)}² + ${fmt(q)}²) = √${fmt(p * p + q * q)}` },
        { label: 'Area of the triangle', value: fmt((p * q) / 2) },
      ];
    },
  },
  'right-triangle-calculator': {
    key: 'right-triangle-calculator',
    title: 'Right Triangle Calculator',
    badge: 'Right triangle',
    note: NOTE,
    fields: [num('a', 'Leg a', '5'), num('b', 'Leg b', '12')],
    compute: ({ a, b }) => {
      if (a <= 0 || b <= 0) return [{ label: 'Triangle', value: 'Enter two positive legs' }];
      const c = Math.hypot(a, b);
      return [
        { label: 'Hypotenuse c', value: fmt(c) },
        { label: 'Angle A (opposite a)', value: `${fmt(deg(Math.atan2(a, b)), 4)}°` },
        { label: 'Angle B (opposite b)', value: `${fmt(deg(Math.atan2(b, a)), 4)}°` },
        { label: 'Area', value: fmt((a * b) / 2) },
        { label: 'Perimeter', value: fmt(a + b + c) },
        { label: 'Height to the hypotenuse', value: fmt((a * b) / c) },
      ];
    },
  },
  'triangle-calculator': {
    key: 'triangle-calculator',
    title: 'Triangle Calculator',
    badge: 'Triangle (SSS)',
    note: NOTE,
    fields: [num('a', 'Side a', '7'), num('b', 'Side b', '8'), num('c', 'Side c', '9')],
    compute: ({ a, b, c }) => {
      if (a <= 0 || b <= 0 || c <= 0 || a + b <= c || a + c <= b || b + c <= a)
        return [
          {
            label: 'Triangle',
            value:
              'These sides cannot form a triangle (each side must be shorter than the other two combined)',
          },
        ];
      const A = deg(Math.acos((b * b + c * c - a * a) / (2 * b * c)));
      const B = deg(Math.acos((a * a + c * c - b * b) / (2 * a * c)));
      const C = 180 - A - B;
      const s = (a + b + c) / 2;
      const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));
      const kind =
        a === b && b === c
          ? 'Equilateral'
          : a === b || b === c || a === c
            ? 'Isosceles'
            : 'Scalene';
      const angleKind =
        Math.abs(Math.max(A, B, C) - 90) < 1e-9
          ? 'right'
          : Math.max(A, B, C) > 90
            ? 'obtuse'
            : 'acute';
      return [
        { label: 'Angle A', value: `${fmt(A, 4)}°` },
        { label: 'Angle B', value: `${fmt(B, 4)}°` },
        { label: 'Angle C', value: `${fmt(C, 4)}°` },
        { label: 'Area (Heron’s formula)', value: fmt(area) },
        { label: 'Perimeter', value: fmt(a + b + c) },
        { label: 'Type', value: `${kind}, ${angleKind}` },
      ];
    },
  },
  'square-root-calculator': {
    key: 'square-root-calculator',
    title: 'Square Root Calculator',
    badge: '√',
    note: NOTE,
    fields: [num('x', 'Number', '72'), num('n', 'Root (2 = square, 3 = cube)', '2')],
    compute: ({ x, n }) => {
      const k = Math.round(n) || 2;
      if (x < 0 && k % 2 === 0)
        return [
          {
            label: 'Root',
            value: `No real ${k === 2 ? 'square' : `${k}th`} root of a negative number (it is ${fmt(Math.pow(-x, 1 / k))}i)`,
          },
        ];
      const r = Math.sign(x) * Math.pow(Math.abs(x), 1 / k);
      const ord =
        k === 3
          ? 'Cube root'
          : `${k}${[, 'st', 'nd', 'rd'][k % 100 > 10 && k % 100 < 14 ? 0 : k % 10] ?? 'th'} root`;
      const lines = [{ label: k === 2 ? `√${fmt(x)}` : `${ord} of ${fmt(x)}`, value: fmt(r, 10) }];
      if (k === 2 && Number.isInteger(x) && x > 0) {
        let outside = 1;
        let inside = x;
        for (let f = Math.floor(Math.sqrt(x)); f > 1; f -= 1) {
          if (x % (f * f) === 0) {
            outside = f;
            inside = x / (f * f);
            break;
          }
        }
        lines.push({
          label: 'Simplified radical',
          value:
            inside === 1
              ? `${outside} (perfect square)`
              : outside > 1
                ? `${outside}√${inside}`
                : `√${x} (already simplest)`,
        });
      }
      lines.push({ label: 'Check', value: `${fmt(r, 10)}^${k} = ${fmt(Math.pow(r, k), 8)}` });
      return lines;
    },
  },
  'circle-calculator': {
    key: 'circle-calculator',
    title: 'Circle Calculator',
    badge: 'Circle',
    note: NOTE,
    fields: [
      select('known', 'I know the', '0', [
        ['Radius', '0'],
        ['Diameter', '1'],
        ['Circumference', '2'],
        ['Area', '3'],
      ]),
      num('v', 'Value', '5'),
    ],
    compute: ({ known, v }) => {
      if (v <= 0) return [{ label: 'Circle', value: 'Enter a positive value' }];
      const r =
        known === 1
          ? v / 2
          : known === 2
            ? v / (2 * Math.PI)
            : known === 3
              ? Math.sqrt(v / Math.PI)
              : v;
      return [
        { label: 'Radius', value: fmt(r) },
        { label: 'Diameter', value: fmt(2 * r) },
        { label: 'Circumference', value: fmt(2 * Math.PI * r) },
        { label: 'Area', value: fmt(Math.PI * r * r) },
      ];
    },
  },
  'area-of-circle-calculator': {
    key: 'area-of-circle-calculator',
    title: 'Area of a Circle Calculator',
    badge: 'πr²',
    note: NOTE,
    fields: [
      select('known', 'I know the', '0', [
        ['Radius', '0'],
        ['Diameter', '1'],
        ['Circumference', '2'],
      ]),
      num('v', 'Value', '10'),
    ],
    compute: ({ known, v }) => {
      if (v <= 0) return [{ label: 'Area', value: 'Enter a positive value' }];
      const r = known === 1 ? v / 2 : known === 2 ? v / (2 * Math.PI) : v;
      return [
        { label: 'Area (πr²)', value: fmt(Math.PI * r * r) },
        { label: 'Working', value: `π × ${fmt(r)}² = ${fmt(Math.PI * r * r)}` },
        { label: 'Radius', value: fmt(r) },
        { label: 'Circumference', value: fmt(2 * Math.PI * r) },
      ];
    },
  },
  'cylinder-volume-calculator': {
    key: 'cylinder-volume-calculator',
    title: 'Cylinder Volume Calculator',
    badge: 'πr²h',
    note: NOTE,
    fields: [
      num('r', 'Radius', '3'),
      num('h', 'Height', '10'),
      select('unit', 'Units', '0', [
        ['Centimeters', '0'],
        ['Inches', '1'],
        ['Meters', '2'],
        ['Feet', '3'],
      ]),
    ],
    compute: ({ r, h, unit }) => {
      const V = Math.PI * r * r * h;
      const u = ['cm', 'in', 'm', 'ft'][unit] ?? 'cm';
      // Litres per cubic unit.
      const litres = V * [0.001, 0.016387064, 1000, 28.316846592][unit];
      return [
        { label: 'Volume', value: `${fmt(V)} ${u}³` },
        { label: 'In litres', value: fmt(litres, 4) },
        { label: 'In US gallons', value: fmt(litres / 3.785411784, 4) },
        { label: 'Lateral surface area', value: `${fmt(2 * Math.PI * r * h)} ${u}²` },
        { label: 'Total surface area', value: `${fmt(2 * Math.PI * r * (r + h))} ${u}²` },
      ];
    },
  },
  'log-calculator': {
    key: 'log-calculator',
    title: 'Log Calculator',
    badge: 'log',
    note: NOTE,
    fields: [num('x', 'Number x', '1000'), num('base', 'Base b', '10')],
    compute: ({ x, base }) => {
      if (x <= 0) return [{ label: 'log', value: 'x must be greater than 0' }];
      if (base <= 0 || base === 1)
        return [{ label: 'log', value: 'The base must be positive and not 1' }];
      return [
        {
          label: `log base ${fmt(base)} of ${fmt(x)}`,
          value: fmt(Math.log(x) / Math.log(base), 10),
        },
        { label: 'Natural log ln(x)', value: fmt(Math.log(x), 10) },
        { label: 'Common log log₁₀(x)', value: fmt(Math.log10(x), 10) },
        { label: 'Binary log log₂(x)', value: fmt(Math.log2(x), 10) },
      ];
    },
  },
  'exponent-calculator': {
    key: 'exponent-calculator',
    title: 'Exponent Calculator',
    badge: 'xⁿ',
    note: NOTE,
    fields: [num('base', 'Base', '2'), num('exp', 'Exponent', '10')],
    compute: ({ base, exp }) => {
      const r = Math.pow(base, exp);
      if (Number.isNaN(r))
        return [
          {
            label: 'Result',
            value: 'A negative base with a fractional exponent has no real result',
          },
        ];
      return [
        { label: `${fmt(base)}^${fmt(exp)}`, value: fmt(r, 10) },
        { label: 'Scientific notation', value: r ? r.toExponential(6) : '0' },
        ...(exp < 0 ? [{ label: 'Same as', value: `1 / ${fmt(base)}^${fmt(-exp)}` }] : []),
      ];
    },
  },
  'rounding-calculator': {
    key: 'rounding-calculator',
    title: 'Rounding Calculator',
    badge: 'Round',
    note: NOTE,
    fields: [
      num('x', 'Number', '3.14159'),
      select('to', 'Round to', '2', [
        ['Whole number', '0'],
        ['1 decimal place (tenths)', '1'],
        ['2 decimal places (hundredths)', '2'],
        ['3 decimal places (thousandths)', '3'],
        ['Nearest 10', '-1'],
        ['Nearest 100', '-2'],
        ['Nearest 1,000', '-3'],
      ]),
    ],
    compute: ({ x, to }) => {
      const f = Math.pow(10, to);
      // Half away from zero (school rounding), with an epsilon so 1.005 rounds to 1.01.
      const half = (Math.sign(x) * Math.round(Math.abs(x) * f + 1e-9)) / f;
      const up = Math.ceil(x * f - 1e-9) / f;
      const down = Math.floor(x * f + 1e-9) / f;
      const fixed = (n: number) => (to > 0 ? n.toFixed(to) : fmt(n));
      return [
        { label: 'Rounded (half up)', value: fixed(half) },
        { label: 'Rounded up (ceiling)', value: fixed(up) },
        { label: 'Rounded down (floor)', value: fixed(down) },
      ];
    },
  },
  'scientific-notation-calculator': {
    key: 'scientific-notation-calculator',
    title: 'Scientific Notation Converter',
    badge: '×10ⁿ',
    note: NOTE,
    fields: [num('x', 'Number', '299792458')],
    compute: ({ x }) => {
      if (!x) return [{ label: 'Scientific notation', value: '0' }];
      const e = Math.floor(Math.log10(Math.abs(x)));
      const m = x / Math.pow(10, e);
      const eng = Math.floor(e / 3) * 3;
      return [
        { label: 'Scientific notation', value: `${fmt(m, 8)} × 10^${e}` },
        { label: 'E notation', value: x.toExponential().replace('e+', 'e') },
        { label: 'Engineering notation', value: `${fmt(x / Math.pow(10, eng), 8)} × 10^${eng}` },
        { label: 'Standard form', value: fmt(x, 12) },
      ];
    },
  },
  'percent-decrease-calculator': {
    key: 'percent-decrease-calculator',
    title: 'Percent Decrease Calculator',
    badge: '% decrease',
    note: NOTE,
    fields: [num('from', 'Original value', '80'), num('to', 'New value', '60')],
    compute: ({ from, to }) => {
      if (!from) return [{ label: 'Percent decrease', value: 'The original value cannot be 0' }];
      const change = ((to - from) / Math.abs(from)) * 100;
      return [
        {
          label: change <= 0 ? 'Percent decrease' : 'Percent increase',
          value: `${fmt(Math.abs(change), 4)}%`,
        },
        { label: 'Difference', value: fmt(to - from) },
        { label: 'Working', value: `(${fmt(from)} − ${fmt(to)}) ÷ ${fmt(from)} × 100` },
      ];
    },
  },
  'long-division-calculator': {
    key: 'long-division-calculator',
    title: 'Long Division Calculator',
    badge: '÷',
    note: NOTE,
    fields: [num('a', 'Dividend', '1234'), num('b', 'Divisor', '7')],
    compute: ({ a, b }) => {
      const x = Math.round(a);
      const y = Math.round(b);
      if (!y) return [{ label: 'Quotient', value: 'Cannot divide by 0' }];
      const q = Math.trunc(x / y);
      const r = x - q * y;
      // Steps: bring down one digit at a time.
      const steps: string[] = [];
      let rem = 0;
      for (const d of String(Math.abs(x))) {
        rem = rem * 10 + Number(d);
        const digit = Math.floor(rem / Math.abs(y));
        if (digit || steps.length)
          steps.push(`${rem} ÷ ${Math.abs(y)} = ${digit}, remainder ${rem - digit * Math.abs(y)}`);
        rem -= digit * Math.abs(y);
      }
      return [
        { label: 'Quotient', value: fmt(q) },
        { label: 'Remainder', value: fmt(r) },
        { label: 'As a mixed number', value: r ? `${q} ${Math.abs(r)}/${Math.abs(y)}` : String(q) },
        { label: 'Decimal', value: fmt(x / y, 8) },
        { label: 'Steps', value: steps.slice(0, 12).join('; ') + (steps.length > 12 ? '; …' : '') },
      ];
    },
  },
  'divisibility-test': {
    key: 'divisibility-test',
    title: 'Divisibility Test',
    badge: 'Divisible?',
    note: NOTE,
    fields: [num('n', 'Whole number', '2520')],
    compute: ({ n }) => {
      const x = Math.abs(Math.round(n));
      const digits = String(x).split('').map(Number);
      const sum = digits.reduce((s, d) => s + d, 0);
      const rule: Record<number, string> = {
        2: 'last digit is even',
        3: `digit sum ${sum} is divisible by 3`,
        4: 'last two digits divisible by 4',
        5: 'ends in 0 or 5',
        6: 'divisible by both 2 and 3',
        7: 'double the last digit, subtract from the rest, repeat',
        8: 'last three digits divisible by 8',
        9: `digit sum ${sum} is divisible by 9`,
        10: 'ends in 0',
        11: 'alternating digit sum divisible by 11',
        12: 'divisible by both 3 and 4',
      };
      return Object.keys(rule).map((k) => {
        const d = Number(k);
        const yes = x % d === 0;
        return {
          label: `Divisible by ${d}?`,
          value: yes ? `Yes (${rule[d]})` : `No, remainder ${x % d}`,
        };
      });
    },
  },
  'wind-chill-calculator': {
    key: 'wind-chill-calculator',
    title: 'Wind Chill Calculator',
    badge: 'Feels like',
    note: NOTE,
    fields: [
      num('t', 'Air temperature', '20'),
      select('tu', 'Temperature unit', '0', [
        ['°F', '0'],
        ['°C', '1'],
      ]),
      num('w', 'Wind speed', '15'),
      select('wu', 'Wind unit', '0', [
        ['mph', '0'],
        ['km/h', '1'],
      ]),
    ],
    compute: ({ t, tu, w, wu }) => {
      const tf = tu === 1 ? (t * 9) / 5 + 32 : t;
      const mph = wu === 1 ? w / 1.609344 : w;
      if (tf > 50 || mph < 3)
        return [
          {
            label: 'Wind chill',
            value: 'Defined only at or below 50°F (10°C) with wind of at least 3 mph (4.8 km/h)',
          },
        ];
      const v = Math.pow(mph, 0.16);
      const wc = 35.74 + 0.6215 * tf - 35.75 * v + 0.4275 * tf * v;
      const c = ((wc - 32) * 5) / 9;
      // Thresholds read off the NWS wind chill chart's frostbite bands.
      const risk =
        wc <= -48
          ? 'Frostbite possible in 5 minutes'
          : wc <= -35
            ? 'Frostbite possible in 10 minutes'
            : wc <= -20
              ? 'Frostbite possible in 30 minutes'
              : 'Low frostbite risk; dress for the cold';
      return [
        { label: 'Wind chill', value: `${fmt(wc, 1)}°F (${fmt(c, 1)}°C)` },
        { label: 'Frostbite risk (NWS)', value: risk },
      ];
    },
  },
  'cooper-test-calculator': {
    key: 'cooper-test-calculator',
    title: 'Cooper Test Calculator',
    badge: 'VO₂ max',
    note: NOTE,
    fields: [
      num('d', 'Distance covered in 12 minutes', '2400'),
      select('u', 'Distance unit', '0', [
        ['Meters', '0'],
        ['Miles', '1'],
      ]),
    ],
    compute: ({ d, u }) => {
      const m = u === 1 ? d * 1609.344 : d;
      const vo2 = (m - 504.9) / 44.73;
      const paceSecPerKm = (12 * 60) / (m / 1000);
      return [
        { label: 'Estimated VO₂ max', value: `${fmt(vo2, 1)} ml/kg/min` },
        { label: 'Distance', value: `${fmt(m, 0)} m (${fmt(m / 1609.344, 2)} mi)` },
        {
          label: 'Average pace',
          value: `${Math.floor(paceSecPerKm / 60)}:${String(Math.round(paceSecPerKm % 60)).padStart(2, '0')} per km`,
        },
      ];
    },
  },
  'surface-area-calculator': {
    key: 'surface-area-calculator',
    title: 'Surface Area Calculator',
    badge: 'Surface area',
    note: NOTE,
    fields: [
      select('shape', 'Shape', '0', [
        ['Cube (side a)', '0'],
        ['Rectangular box (a × b × c)', '1'],
        ['Sphere (radius a)', '2'],
        ['Cylinder (radius a, height b)', '3'],
        ['Cone (radius a, height b)', '4'],
      ]),
      num('a', 'a', '3'),
      num('b', 'b', '4'),
      num('c', 'c', '5'),
    ],
    compute: ({ shape, a, b, c }) => {
      const pi = Math.PI;
      const [sa, vol, how] =
        shape === 0
          ? [6 * a * a, a ** 3, '6a²']
          : shape === 1
            ? [2 * (a * b + b * c + a * c), a * b * c, '2(ab + bc + ac)']
            : shape === 2
              ? [4 * pi * a * a, (4 / 3) * pi * a ** 3, '4πr²']
              : shape === 3
                ? [2 * pi * a * (a + b), pi * a * a * b, '2πr(r + h)']
                : [pi * a * (a + Math.hypot(a, b)), (pi * a * a * b) / 3, 'πr(r + √(r² + h²))'];
      return [
        { label: 'Surface area', value: fmt(sa) },
        { label: 'Formula', value: how },
        { label: 'Volume', value: fmt(vol) },
      ];
    },
  },
  'volumetric-weight-calculator': {
    key: 'volumetric-weight-calculator',
    title: 'Volumetric Weight Calculator',
    badge: 'Dim weight',
    note: NOTE,
    fields: [
      num('l', 'Length', '50'),
      num('w', 'Width', '40'),
      num('h', 'Height', '30'),
      select('div', 'Carrier divisor', '5000', [
        ['cm, ÷ 5000 (DHL, UPS, FedEx international)', '5000'],
        ['cm, ÷ 6000 (many postal and economy services)', '6000'],
        ['cm, ÷ 4000 (some couriers)', '4000'],
        ['inches, ÷ 139 (US domestic, lb)', '139'],
      ]),
      num('actual', 'Actual weight (kg, or lb for inches)', '8'),
    ],
    compute: ({ l, w, h, div, actual }) => {
      const vol = (l * w * h) / div;
      const unit = div === 139 ? 'lb' : 'kg';
      return [
        { label: 'Volumetric (dimensional) weight', value: `${fmt(vol, 2)} ${unit}` },
        {
          label: 'Chargeable weight (greater of the two)',
          value: `${fmt(Math.max(vol, actual), 2)} ${unit}`,
        },
        {
          label: 'Billed on',
          value:
            vol > actual ? 'Volumetric weight: the box is bulky for its weight' : 'Actual weight',
        },
      ];
    },
  },
  'tank-volume-calculator': {
    key: 'tank-volume-calculator',
    title: 'Tank Volume Calculator',
    badge: 'Tank',
    note: NOTE,
    fields: [
      select('shape', 'Tank shape', '0', [
        ['Vertical cylinder (diameter, height)', '0'],
        ['Rectangular (length, width, height)', '1'],
        ['Horizontal cylinder (diameter, length)', '2'],
      ]),
      num('a', 'Diameter or length', '100'),
      num('b', 'Height, width or length', '150'),
      num('c', 'Height (rectangular only)', '0'),
      select('unit', 'Units', '0', [
        ['Centimeters', '0'],
        ['Inches', '1'],
        ['Meters', '2'],
        ['Feet', '3'],
      ]),
    ],
    compute: ({ shape, a, b, c, unit }) => {
      const cube = shape === 1 ? a * b * c : Math.PI * (a / 2) ** 2 * b;
      const litres = cube * [0.001, 0.016387064, 1000, 28.316846592][unit];
      return [
        { label: 'Capacity in litres', value: fmt(litres, 2) },
        { label: 'Capacity in US gallons', value: fmt(litres / 3.785411784, 2) },
        { label: 'Capacity in UK gallons', value: fmt(litres / 4.54609, 2) },
        { label: 'Volume in cubic units', value: fmt(cube, 2) },
      ];
    },
  },
  'pool-volume-calculator': {
    key: 'pool-volume-calculator',
    title: 'Pool Volume Calculator',
    badge: 'Pool',
    note: NOTE,
    fields: [
      select('shape', 'Pool shape', '0', [
        ['Rectangular', '0'],
        ['Round', '1'],
        ['Oval', '2'],
      ]),
      num('a', 'Length or diameter', '32'),
      num('b', 'Width (not used for round)', '16'),
      num('shallow', 'Shallow-end depth', '3.5'),
      num('deep', 'Deep-end depth', '8'),
      select('unit', 'Units', '0', [
        ['Feet', '0'],
        ['Meters', '1'],
      ]),
    ],
    compute: ({ shape, a, b, shallow, deep, unit }) => {
      const avg = (shallow + deep) / 2;
      const area =
        shape === 0 ? a * b : shape === 1 ? Math.PI * (a / 2) ** 2 : Math.PI * (a / 2) * (b / 2);
      const cube = area * avg;
      const litres = unit === 1 ? cube * 1000 : cube * 28.316846592;
      return [
        { label: 'Water volume (US gallons)', value: fmt(litres / 3.785411784, 0) },
        { label: 'Water volume (litres)', value: fmt(litres, 0) },
        { label: 'Average depth', value: `${fmt(avg, 2)} ${unit === 1 ? 'm' : 'ft'}` },
        { label: 'Surface area', value: `${fmt(area, 1)} ${unit === 1 ? 'm²' : 'sq ft'}` },
      ];
    },
  },
  'square-meters-calculator': {
    key: 'square-meters-calculator',
    title: 'Square Meters Calculator',
    badge: 'm²',
    note: NOTE,
    fields: [
      num('l', 'Length', '5'),
      num('w', 'Width', '4'),
      select('unit', 'Measured in', '0', [
        ['Meters', '0'],
        ['Centimeters', '1'],
        ['Feet', '2'],
        ['Inches', '3'],
      ]),
      num('count', 'Number of identical areas', '1'),
    ],
    compute: ({ l, w, unit, count }) => {
      const m = [1, 0.01, 0.3048, 0.0254][unit];
      const sqm = l * m * w * m * Math.max(1, count);
      return [
        { label: 'Area in square meters', value: `${fmt(sqm, 3)} m²` },
        { label: 'Area in square feet', value: `${fmt(sqm * 10.7639104, 2)} sq ft` },
        { label: 'Area in square yards', value: `${fmt(sqm * 1.19599005, 2)} sq yd` },
      ];
    },
  },
  'steps-to-distance-calculator': {
    key: 'steps-to-distance-calculator',
    title: 'Steps to Distance Calculator',
    badge: 'Steps',
    note: NOTE,
    fields: [
      num('steps', 'Steps', '10000'),
      num('height', 'Your height (cm)', '175'),
      select('sex', 'Sex (for stride estimate)', '0', [
        ['Male', '0'],
        ['Female', '1'],
      ]),
    ],
    compute: ({ steps, height, sex }) => {
      // Common walking-stride estimate: height × 0.415 (men) or × 0.413 (women).
      const stride = (height * (sex === 1 ? 0.413 : 0.415)) / 100;
      const km = (steps * stride) / 1000;
      return [
        { label: 'Distance', value: `${fmt(km, 2)} km (${fmt(km / 1.609344, 2)} miles)` },
        { label: 'Estimated stride length', value: `${fmt(stride * 100, 1)} cm` },
        { label: 'Steps per km at this stride', value: fmt(1000 / stride, 0) },
      ];
    },
  },
  'scale-calculator': {
    key: 'scale-calculator',
    title: 'Scale Calculator',
    badge: '1:N',
    note: NOTE,
    fields: [
      select('dir', 'Convert', '0', [
        ['Real size → model size', '0'],
        ['Model size → real size', '1'],
      ]),
      num('size', 'Size', '4.5'),
      num('n', 'Scale 1 :', '87'),
    ],
    compute: ({ dir, size, n }) => {
      if (n <= 0) return [{ label: 'Result', value: 'Enter a positive scale' }];
      const out = dir === 1 ? size * n : size / n;
      return [
        { label: dir === 1 ? 'Real size' : 'Model size', value: `${fmt(out, 4)} (same unit)` },
        { label: 'Scale factor', value: `1:${fmt(n)} = ${fmt(100 / n, 4)}%` },
      ];
    },
  },
  'dog-age-calculator': {
    key: 'dog-age-calculator',
    title: 'Dog Age Calculator',
    badge: 'Dog years',
    note: NOTE,
    fields: [
      num('age', 'Dog’s age (years)', '5'),
      select('size', 'Size', '1', [
        ['Small (under 20 lb / 9 kg)', '0'],
        ['Medium (21-50 lb / 9-23 kg)', '1'],
        ['Large (51-100 lb / 23-45 kg)', '2'],
        ['Giant (over 100 lb / 45 kg)', '3'],
      ]),
    ],
    compute: ({ age, size }) => {
      if (age <= 0) return [{ label: 'Human years', value: 'Enter the dog’s age' }];
      // AVMA: year one ≈ 15 human years, year two ≈ 9 more. After that published charts
      // add roughly 4 (small) to 7 (giant) a year; big dogs age faster later in life.
      const later = [4, 5, 6, 7][size] ?? 5;
      const human = age <= 1 ? 15 * age : age <= 2 ? 15 + 9 * (age - 1) : 24 + later * (age - 2);
      const epigenetic = 16 * Math.log(age) + 31;
      return [
        { label: 'Human age (15 + 9, then by size)', value: `${fmt(human, 1)} years` },
        {
          label: 'Human age (UCSD epigenetic clock, 16 ln(age) + 31)',
          value: age >= 1 ? `${fmt(epigenetic, 1)} years` : 'Formula applies from age 1',
        },
        { label: 'The old “× 7” rule', value: `${fmt(age * 7, 1)} years (least accurate)` },
      ];
    },
  },
  'cat-age-calculator': {
    key: 'cat-age-calculator',
    title: 'Cat Age Calculator',
    badge: 'Cat years',
    note: NOTE,
    fields: [num('age', 'Cat’s age (years)', '8')],
    compute: ({ age }) => {
      if (age <= 0) return [{ label: 'Human years', value: 'Enter the cat’s age' }];
      // AAHA/AAFP life-stage chart: 15 at one, 24 at two, then +4 per year.
      const human = age <= 1 ? 15 * age : age <= 2 ? 15 + 9 * (age - 1) : 24 + 4 * (age - 2);
      const stage =
        age < 1 ? 'Kitten' : age < 7 ? 'Young adult' : age < 11 ? 'Mature adult' : 'Senior';
      return [
        { label: 'Human age', value: `${fmt(human, 1)} years` },
        { label: 'Life stage (AAHA/AAFP)', value: stage },
      ];
    },
  },
  'overtime-calculator': {
    key: 'overtime-calculator',
    title: 'Overtime Calculator',
    badge: 'Overtime',
    note: NOTE,
    fields: [
      num('rate', 'Hourly rate', '22'),
      num('hours', 'Hours worked this week', '48'),
      num('threshold', 'Overtime after (hours/week)', '40'),
      num('mult', 'Overtime multiplier', '1.5'),
    ],
    compute: ({ rate, hours, threshold, mult }) => {
      const reg = Math.min(hours, threshold);
      const ot = Math.max(0, hours - threshold);
      const regPay = reg * rate;
      const otPay = ot * rate * mult;
      return [
        { label: 'Total pay', value: fmt(regPay + otPay, 2) },
        { label: 'Regular pay', value: `${fmt(reg, 2)} h × ${fmt(rate, 2)} = ${fmt(regPay, 2)}` },
        {
          label: 'Overtime pay',
          value: `${fmt(ot, 2)} h × ${fmt(rate * mult, 2)} = ${fmt(otPay, 2)}`,
        },
        { label: 'Overtime rate', value: fmt(rate * mult, 2) },
      ];
    },
  },
  'speed-distance-time-calculator': {
    key: 'speed-distance-time-calculator',
    title: 'Speed Distance Time Calculator',
    badge: 'd = s × t',
    note: NOTE,
    fields: [
      select('solve', 'Solve for', '0', [
        ['Speed (from distance and time)', '0'],
        ['Distance (from speed and time)', '1'],
        ['Time (from distance and speed)', '2'],
      ]),
      num('d', 'Distance (km or miles)', '150'),
      num('s', 'Speed (km/h or mph)', '60'),
      num('t', 'Time (hours, e.g. 2.5)', '2.5'),
    ],
    compute: ({ solve, d, s, t }) => {
      const hm = (h: number) => `${Math.floor(h)} h ${Math.round((h % 1) * 60)} min`;
      if (solve === 1)
        return [
          { label: 'Distance', value: fmt(s * t, 3) },
          { label: 'Working', value: `${fmt(s)} × ${fmt(t)} h` },
        ];
      if (solve === 2) {
        if (!s) return [{ label: 'Time', value: 'Speed cannot be 0' }];
        return [
          { label: 'Time', value: `${fmt(d / s, 4)} h (${hm(d / s)})` },
          { label: 'Working', value: `${fmt(d)} ÷ ${fmt(s)}` },
        ];
      }
      if (!t) return [{ label: 'Speed', value: 'Time cannot be 0' }];
      return [
        { label: 'Average speed', value: fmt(d / t, 3) },
        { label: 'Working', value: `${fmt(d)} ÷ ${fmt(t)} h` },
      ];
    },
  },
  'gas-mileage-calculator': {
    key: 'gas-mileage-calculator',
    title: 'Gas Mileage Calculator',
    badge: 'MPG',
    note: NOTE,
    fields: [
      num('dist', 'Distance driven since the last fill-up', '320'),
      num('fuel', 'Fuel used to refill', '11.5'),
      select('unit', 'Units', '0', [
        ['Miles and US gallons', '0'],
        ['Kilometres and litres', '1'],
      ]),
      num('price', 'Fuel price per gallon or litre (optional)', '3.4'),
    ],
    compute: ({ dist, fuel, unit, price }) => {
      if (!fuel || !dist)
        return [{ label: 'Mileage', value: 'Enter the distance and the fuel used' }];
      const miles = unit === 1 ? dist / 1.609344 : dist;
      const gallons = unit === 1 ? fuel / 3.785411784 : fuel;
      const mpg = miles / gallons;
      const lPer100 = 235.214583 / mpg;
      const lines = [
        { label: 'Miles per gallon (US)', value: fmt(mpg, 2) },
        { label: 'Litres per 100 km', value: fmt(lPer100, 2) },
        { label: 'Kilometres per litre', value: fmt((mpg * 1.609344) / 3.785411784, 2) },
      ];
      if (price > 0)
        lines.push({
          label: `Cost per ${unit === 1 ? 'km' : 'mile'}`,
          value: fmt((fuel * price) / dist, 3),
        });
      return lines;
    },
  },
  'square-footage-calculator': {
    key: 'square-footage-calculator',
    title: 'Square Footage Calculator',
    badge: 'sq ft',
    note: NOTE,
    fields: [
      select('shape', 'Shape', '0', [
        ['Rectangle (length × width)', '0'],
        ['Circle (diameter in A)', '1'],
        ['Triangle (base × height)', '2'],
      ]),
      num('a', 'Length / diameter / base', '12'),
      num('b', 'Width / height', '15'),
      select('unit', 'Measured in', '0', [
        ['Feet', '0'],
        ['Inches', '1'],
        ['Meters', '2'],
        ['Yards', '3'],
      ]),
      num('price', 'Price per sq ft (optional)', '0'),
    ],
    compute: ({ shape, a, b, unit, price }) => {
      const toFt = [1, 1 / 12, 3.280839895, 3][unit];
      const A = a * toFt;
      const B = b * toFt;
      const sqft = shape === 1 ? Math.PI * (A / 2) ** 2 : shape === 2 ? (A * B) / 2 : A * B;
      const lines = [
        { label: 'Area in square feet', value: `${fmt(sqft, 2)} sq ft` },
        { label: 'Area in square meters', value: `${fmt(sqft * 0.09290304, 2)} m²` },
        { label: 'Area in square yards', value: `${fmt(sqft / 9, 2)} sq yd` },
      ];
      if (price > 0) lines.push({ label: 'Total cost', value: fmt(sqft * price, 2) });
      return lines;
    },
  },
  'acreage-calculator': {
    key: 'acreage-calculator',
    title: 'Acreage Calculator',
    badge: 'Acres',
    note: NOTE,
    fields: [
      num('l', 'Length', '660'),
      num('w', 'Width', '330'),
      select('unit', 'Measured in', '0', [
        ['Feet', '0'],
        ['Meters', '1'],
        ['Yards', '2'],
        ['Miles', '3'],
      ]),
    ],
    compute: ({ l, w, unit }) => {
      const toFt = [1, 3.280839895, 3, 5280][unit];
      const sqft = l * toFt * w * toFt;
      return [
        { label: 'Acres', value: fmt(sqft / 43560, 4) },
        { label: 'Hectares', value: fmt((sqft * 0.09290304) / 10000, 4) },
        { label: 'Square feet', value: fmt(sqft, 0) },
        { label: 'Square meters', value: fmt(sqft * 0.09290304, 0) },
      ];
    },
  },
  'cubic-feet-calculator': {
    key: 'cubic-feet-calculator',
    title: 'Cubic Feet Calculator',
    badge: 'ft³',
    note: NOTE,
    fields: [
      num('l', 'Length', '48'),
      num('w', 'Width', '24'),
      num('h', 'Height', '18'),
      select('unit', 'Measured in', '1', [
        ['Feet', '0'],
        ['Inches', '1'],
        ['Centimeters', '2'],
        ['Meters', '3'],
        ['Yards', '4'],
      ]),
      num('qty', 'Quantity', '1'),
    ],
    compute: ({ l, w, h, unit, qty }) => {
      const toFt = [1, 1 / 12, 1 / 30.48, 3.280839895, 3][unit];
      const cf = l * toFt * w * toFt * h * toFt * Math.max(1, qty);
      return [
        { label: 'Cubic feet', value: `${fmt(cf, 3)} ft³` },
        { label: 'Cubic yards', value: `${fmt(cf / 27, 3)} yd³` },
        { label: 'Cubic meters', value: `${fmt(cf * 0.028316846592, 4)} m³` },
        { label: 'Litres', value: fmt(cf * 28.316846592, 1) },
      ];
    },
  },
  'tire-size-calculator': {
    key: 'tire-size-calculator',
    title: 'Tire Size Calculator',
    badge: 'Tire size',
    note: NOTE,
    fields: [
      num('w1', 'Current width (mm), e.g. 225', '225'),
      num('a1', 'Current aspect ratio (%), e.g. 45', '45'),
      num('r1', 'Current rim (inches), e.g. 17', '17'),
      num('w2', 'New width (mm)', '235'),
      num('a2', 'New aspect ratio (%)', '40'),
      num('r2', 'New rim (inches)', '18'),
    ],
    compute: ({ w1, a1, r1, w2, a2, r2 }) => {
      const dia = (w: number, a: number, r: number) => (2 * w * (a / 100)) / 25.4 + r;
      const d1 = dia(w1, a1, r1);
      const d2 = dia(w2, a2, r2);
      const diff = (d2 - d1) / d1;
      const revs = (d: number) => 63360 / (Math.PI * d);
      return [
        {
          label: `${w1}/${a1}R${r1} diameter`,
          value: `${fmt(d1, 2)} in (${fmt(d1 * 25.4, 0)} mm)`,
        },
        {
          label: `${w2}/${a2}R${r2} diameter`,
          value: `${fmt(d2, 2)} in (${fmt(d2 * 25.4, 0)} mm)`,
        },
        {
          label: 'Diameter difference',
          value: `${diff >= 0 ? '+' : ''}${fmt(diff * 100, 2)}%${Math.abs(diff) > 0.03 ? ' (over the usual ±3% guideline)' : ''}`,
        },
        {
          label: 'Speedometer at 60 mph shows 60, real speed',
          value: `${fmt(60 * (1 + diff), 1)} mph`,
        },
        {
          label: 'Sidewall height (current → new)',
          value: `${fmt((w1 * a1) / 100 / 25.4, 2)} in → ${fmt((w2 * a2) / 100 / 25.4, 2)} in`,
        },
        {
          label: 'Revolutions per mile (current → new)',
          value: `${fmt(revs(d1), 0)} → ${fmt(revs(d2), 0)}`,
        },
      ];
    },
  },
};
